// src/stores/WidgetStore.js - Simplified Zustand Store for Pattern B
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const useWidgetStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // SIMPLIFIED STATE (Pattern B)
    // ================================
    
    // Main widget data - single source of truth
    widgets: [],
    widgetsLoading: false,

    // Selected widget state (for widget details/editing)
    selectedWidget: null,
    selectedWidgetLoading: false,

    // Simple operation states
    creatingWidget: false,
    savingWidget: false,
    publishingWidget: false,
    deletingWidget: false,
    
    // Embed code state
    embedCodes: {}, // Keyed by widget_id
    generatingEmbed: false,
    
    // Filter state (for widget type filtering)
    selectedType: 'all', // 'all', 'booking', 'chat', 'contact'

    // Connection state
    isConnected: false,

    // Internal flags
    _initialized: false,

    // ================================
    // EDITING STATE (Real-time Preview)
    // ================================

    // Editing session state - single source of truth for widget editing
    editing: {
      widgetId: null,              // ID of widget currently being edited
      draft: null,                 // Working copy of widget config (draft_configuration + other fields)
      previewState: {              // UI state for preview widget (ephemeral, not saved)
        selectedService: null,
        currentStep: 'service',
        selectedDate: null,
        selectedTimeSlot: null,
        selectedDateTime: null,
        serviceAddress: '',
        customerInfo: null
      },
      isDirty: false,              // Has unsaved changes to draft
      lastAutoSave: null           // Timestamp of last auto-save
    },

    // Internal auto-save timer
    _autoSaveTimer: null,
    
    // ================================
    // SOCKET OPERATIONS (Simplified)
    // ================================
    
    /**
     * Request all widgets for the business - NO RETURN VALUE
     * @param {string} widgetType - Optional filter by widget type
     * @param {object} options - Additional options (force, etc.)
     */
    loadWidgets: (widgetType = null, options = {}) => {
      const { widgetsLoading } = get();
      
      if (widgetsLoading && !options.force) {
        console.log('WidgetStore: Already loading widgets, skipping');
        return;
      }
      
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      console.log('WidgetStore: Loading widgets:', widgetType || 'all types');
      
      set({
        widgetsLoading: true
      });
      
      const requestData = {};
      if (widgetType) {
        requestData.widget_type = widgetType;
      }
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_WIDGETS, requestData);
    },

    /**
     * Request detailed information for a specific widget - NO RETURN VALUE
     * @param {string} widgetId - Widget ID
     */
    loadWidgetDetails: (widgetId) => {
      const { selectedWidgetLoading } = get();
      
      if (selectedWidgetLoading) {
        console.log('WidgetStore: Already loading widget details, skipping');
        return;
      }
      
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      if (!widgetId) {
        console.error('Widget ID is required for loadWidgetDetails');
        return;
      }

      console.log('WidgetStore: Loading widget details:', widgetId);
      
      set({
        selectedWidgetLoading: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_WIDGET_DETAILS, {
        widget_id: widgetId
      });
    },

    /**
     * Create a new widget (starts as inactive) - NO RETURN VALUE
     * @param {object} widgetData - Widget configuration
     */
    createWidget: (widgetData) => {
      const { creatingWidget } = get();
      
      if (creatingWidget) {
        console.log('WidgetStore: Already creating widget, skipping');
        return;
      }
      
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      if (!widgetData.widget_type || !widgetData.widget_name) {
        console.error('Widget type and name are required for createWidget');
        return;
      }

      console.log('WidgetStore: Creating widget:', widgetData.widget_name, `(${widgetData.widget_type})`);
      
      set({
        creatingWidget: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.CREATE_WIDGET, widgetData);
    },

    /**
     * Update an existing widget - NO RETURN VALUE
     * @param {string} widgetId - Widget ID
     * @param {object} updateData - Fields to update
     */
    updateWidget: (widgetId, updateData) => {
      const { savingWidget } = get();
      
      if (savingWidget) {
        console.log('WidgetStore: Already saving widget, skipping');
        return;
      }
      
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      if (!widgetId) {
        console.error('Widget ID is required for updateWidget');
        return;
      }

      console.log('WidgetStore: Updating widget:', widgetId);
      
      set({
        savingWidget: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_WIDGET, {
        widget_id: widgetId,
        updates: updateData
      });
    },

    /**
     * Publish widget (copy draft config to published config) - NO RETURN VALUE
     * @param {string} widgetId - Widget ID
     */
    publishWidget: (widgetId) => {
      const { publishingWidget } = get();

      if (publishingWidget) {
        console.log('WidgetStore: Already publishing widget, skipping');
        return;
      }

      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }

      if (!widgetId) {
        console.error('Widget ID is required for publishWidget');
        return;
      }

      console.log('WidgetStore: Publishing widget:', widgetId);

      set({
        publishingWidget: true
      });

      socketManager.universalHandler.emit(SOCKET_EVENTS.PUBLISH_WIDGET, {
        widget_id: widgetId
      });
    },

    /**
     * Toggle widget active status - NO RETURN VALUE
     * @param {string} widgetId - Widget ID
     * @param {boolean} isActive - New active status
     */
    toggleWidgetStatus: (widgetId, isActive) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      if (!widgetId || isActive === undefined) {
        console.error('Widget ID and active status are required for toggleWidgetStatus');
        return;
      }

      console.log('WidgetStore: Toggling widget status:', widgetId, isActive ? 'activate' : 'deactivate');

      socketManager.universalHandler.emit(SOCKET_EVENTS.TOGGLE_WIDGET_STATUS, {
        widget_id: widgetId,
        is_active: isActive
      });
    },

    /**
     * Delete a widget - NO RETURN VALUE
     * @param {string} widgetId - Widget ID
     */
    deleteWidget: (widgetId) => {
      const { deletingWidget } = get();
      
      if (deletingWidget) {
        console.log('WidgetStore: Already deleting widget, skipping');
        return;
      }
      
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      if (!widgetId) {
        console.error('Widget ID is required for deleteWidget');
        return;
      }

      console.log('WidgetStore: Deleting widget:', widgetId);

      set({
        deletingWidget: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.DELETE_WIDGET, {
        widget_id: widgetId
      });
    },

    /**
     * Generate embed code for a widget - NO RETURN VALUE
     * @param {string} widgetId - Widget ID
     */
    generateEmbedCode: (widgetId) => {
      const { generatingEmbed, embedCodes } = get();
      
      if (generatingEmbed) {
        console.log('WidgetStore: Already generating embed code, skipping');
        return;
      }
      
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ WidgetStore: Socket service not initialized');
        return;
      }
      
      if (!widgetId) {
        console.error('Widget ID is required for generateEmbedCode');
        return;
      }

      // Check if we already have it cached
      const cached = embedCodes[widgetId];
      if (cached) {
        console.log('WidgetStore: Using cached embed code for widget:', widgetId);
        return;
      }

      console.log('WidgetStore: Generating embed code:', widgetId);

      set({
        generatingEmbed: true
      });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_EMBED_CODE, {
        widget_id: widgetId
      });
    },

    // ================================
    // EDITING ACTIONS (Real-time Preview)
    // ================================

    /**
     * Start editing a widget - initializes editing session
     * @param {string} widgetId - Widget ID to edit
     */
    startEditing: (widgetId) => {
      const { selectedWidget, widgets } = get();

      // Get the widget data
      const widget = selectedWidget?.id === widgetId
        ? selectedWidget
        : widgets.find(w => w.id === widgetId);

      if (!widget) {
        console.error('WidgetStore: Cannot start editing - widget not found:', widgetId);
        return;
      }

      console.log('WidgetStore: Starting editing session for widget:', widgetId);

      // Initialize editing state from draft_configuration
      set({
        editing: {
          widgetId: widgetId,
          draft: {
            widget_name: widget.widget_name || '',
            widget_type: widget.widget_type || 'booking',
            is_active: widget.is_active ?? false,
            primary_color: widget.primary_color || '#007bff',
            theme: widget.theme || 'auto',
            border_radius: widget.border_radius || 8,
            configuration: {
              ...widget.draft_configuration
            }
          },
          previewState: {
            selectedService: null,
            currentStep: 'service',
            selectedDate: null,
            selectedTimeSlot: null,
            selectedDateTime: null,
            serviceAddress: '',
            customerInfo: null
          },
          isDirty: false,
          lastAutoSave: Date.now()
        }
      });
    },

    /**
     * Update editing draft configuration (triggers auto-save)
     * @param {object} updates - Fields to update in draft
     */
    updateEditingDraft: (updates) => {
      const { editing } = get();

      if (!editing.widgetId) {
        console.error('WidgetStore: Cannot update draft - no editing session active');
        return;
      }

      console.log('WidgetStore: Updating draft:', updates);

      // Update draft and mark as dirty
      set(state => ({
        editing: {
          ...state.editing,
          draft: {
            ...state.editing.draft,
            ...updates,
            // Handle nested configuration updates
            configuration: updates.configuration
              ? { ...state.editing.draft.configuration, ...updates.configuration }
              : state.editing.draft.configuration
          },
          isDirty: true
        }
      }));

      // Schedule auto-save
      get()._scheduleAutoSave();
    },

    /**
     * Update preview state (does NOT trigger auto-save - ephemeral UI state)
     * @param {object} updates - Fields to update in preview state
     */
    updatePreviewState: (updates) => {
      const { editing } = get();

      if (!editing.widgetId) {
        console.error('WidgetStore: Cannot update preview state - no editing session active');
        return;
      }

      console.log('WidgetStore: Updating preview state:', updates);

      set(state => ({
        editing: {
          ...state.editing,
          previewState: {
            ...state.editing.previewState,
            ...updates
          }
        }
      }));
    },

    /**
     * Stop editing session - clear editing state
     */
    stopEditing: () => {
      const { _autoSaveTimer } = get();

      // Clear any pending auto-save
      if (_autoSaveTimer) {
        clearTimeout(_autoSaveTimer);
      }

      console.log('WidgetStore: Stopping editing session');

      set({
        editing: {
          widgetId: null,
          draft: null,
          previewState: {
            selectedService: null,
            currentStep: 'service',
            selectedDate: null,
            selectedTimeSlot: null,
            selectedDateTime: null,
            serviceAddress: '',
            customerInfo: null
          },
          isDirty: false,
          lastAutoSave: null
        },
        _autoSaveTimer: null
      });
    },

    /**
     * Internal: Schedule auto-save with debounce
     * @private
     */
    _scheduleAutoSave: () => {
      const store = get();

      // Clear existing timer
      if (store._autoSaveTimer) {
        clearTimeout(store._autoSaveTimer);
      }

      // Schedule new auto-save after 2 seconds
      const timer = setTimeout(() => {
        const { editing } = get();

        if (editing.isDirty && editing.widgetId && editing.draft) {
          console.log('WidgetStore: Auto-saving draft changes...');

          // Call existing updateWidget operation — isDirty reset handled by _updateWidget response handler
          get().updateWidget(editing.widgetId, {
            widget_name: editing.draft.widget_name,
            is_active: editing.draft.is_active,
            primary_color: editing.draft.primary_color,
            theme: editing.draft.theme,
            border_radius: editing.draft.border_radius,
            configuration: editing.draft.configuration
          });
        }
      }, 2000);

      set({ _autoSaveTimer: timer });
    },

    // ================================
    // UTILITY FUNCTIONS (Simplified)
    // ================================
    
    clearSelectedWidget: () => {
      set({
        selectedWidget: null,
        selectedWidgetLoading: false
      });
    },
    
    refreshWidgets: () => {
      const { selectedType } = get();
      console.log('WidgetStore: Refreshing widgets');
      
      // Force reload
      get().loadWidgets(selectedType === 'all' ? null : selectedType, { force: true });
    },
    
    getWidgetById: (widgetId) => {
      const { widgets } = get();
      return widgets.find(w => w.id === widgetId) || null;
    },
    
    getWidgetsByType: (widgetType) => {
      const { widgets } = get();
      return widgets.filter(w => w.widget_type === widgetType);
    },
    
    getEmbedCode: (widgetId) => {
      const { embedCodes } = get();
      return embedCodes[widgetId] || null;
    },
    
    setSelectedType: (type) => {
      set({ selectedType: type });
    },
    
    // ================================
    // COMPUTED VALUES (Simplified)
    // ================================
    
    getFilteredWidgets: () => {
      const { widgets, selectedType } = get();
      return selectedType === 'all' 
        ? widgets 
        : widgets.filter(w => w.widget_type === selectedType);
    },
    
    getWidgetCounts: () => {
      const { widgets } = get();
      return widgets.reduce((acc, widget) => {
        acc[widget.widget_type] = (acc[widget.widget_type] || 0) + 1;
        return acc;
      }, {});
    },
    
    getHasWidgets: () => {
      const { widgets } = get();
      return widgets.length > 0;
    },
    
    getHasActiveWidgets: () => {
      const { widgets } = get();
      return widgets.some(w => w.is_active);
    },
    
    // ================================
    // INTERNAL STATE UPDATERS (Simplified)
    // ================================
    
    _setWidgetsLoaded: (data) => {
      console.log('WidgetStore: Widgets list received');
      
      const widgetsData = Array.isArray(data) ? data : [];
      
      set({
        widgets: widgetsData,
        widgetsLoading: false
      });
    },
    
    _setWidgetDetailsLoaded: (data) => {
      console.log('WidgetStore: Widget details received');
      
      set({
        selectedWidget: data,
        selectedWidgetLoading: false
      });
    },
    
    _addWidget: (widgetData) => {
      console.log('WidgetStore: Widget created');
      
      set(state => ({
        widgets: [...state.widgets, widgetData],
        creatingWidget: false,
        // Set as selected widget
        selectedWidget: widgetData
      }));
    },
    
    _updateWidget: (widgetData) => {
      console.log('WidgetStore: Widget updated');

      set(state => {
        const updatedWidgets = state.widgets.map(widget =>
          widget.id === widgetData.id ? widgetData : widget
        );

        // Reset isDirty on server confirmation (if editing this widget)
        const updatedEditing = state.editing.widgetId === widgetData.id
          ? { ...state.editing, isDirty: false, lastAutoSave: Date.now() }
          : state.editing;

        return {
          widgets: updatedWidgets,
          savingWidget: false,
          editing: updatedEditing,
          // Update selected widget if it's the same one
          selectedWidget: state.selectedWidget && state.selectedWidget.id === widgetData.id
            ? widgetData
            : state.selectedWidget
        };
      });
    },

    _publishWidget: (widgetData) => {
      console.log('WidgetStore: Widget published');

      set(state => {
        const updatedWidgets = state.widgets.map(widget =>
          widget.id === widgetData.id ? widgetData : widget
        );

        return {
          widgets: updatedWidgets,
          publishingWidget: false,
          // Update selected widget if it's the same one
          selectedWidget: state.selectedWidget && state.selectedWidget.id === widgetData.id
            ? widgetData
            : state.selectedWidget
        };
      });
    },

    // UPDATED: Enhanced widget deletion handling
    _removeWidget: (widgetId) => {
      console.log('WidgetStore: Widget deleted:', widgetId);
      
      set(state => {
        const isSelectedWidget = state.selectedWidget && state.selectedWidget.id === widgetId;
        
        // Log if we're clearing the selected widget
        if (isSelectedWidget) {
          console.log('WidgetStore: Clearing selected widget (was deleted)');
        }
        
        const updatedWidgets = state.widgets.filter(widget => widget.id !== widgetId);
        const updatedEmbedCodes = { ...state.embedCodes };
        delete updatedEmbedCodes[widgetId];
        
        return {
          widgets: updatedWidgets,
          deletingWidget: false,
          embedCodes: updatedEmbedCodes,
          // IMPORTANT: Clear selected widget if it was deleted
          selectedWidget: isSelectedWidget ? null : state.selectedWidget,
          // Also clear any related selected widget state
          selectedWidgetLoading: isSelectedWidget ? false : state.selectedWidgetLoading
        };
      });
    },
    
    _setEmbedCodeGenerated: (data) => {
      console.log('WidgetStore: Embed code generated');
      
      set(state => ({
        embedCodes: {
          ...state.embedCodes,
          [data.widget_id]: data
        },
        generatingEmbed: false
      }));
    },
    
    // ================================
    // CONNECTION MANAGEMENT
    // ================================
    
    _setConnectionState: (isConnected) => {
      set({ isConnected });
    },
    
    _getSocketManager: () => {
      if (typeof window !== 'undefined' && window.__socketManager) {
        return { socketManager: window.__socketManager };
      }
      return { socketManager: null };
    },
    
    // ================================
    // INITIALIZATION
    // ================================
    
    _initialize: (socketManager) => {
      const state = get();
      
      console.log('WidgetStore: Initializing with socket manager');
      
      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }
      
      set({ 
        _initialized: true,
        isConnected: socketManager?.isAuthenticated || false 
      });
      
      // Auto-load widgets if authenticated
      if (socketManager?.isAuthenticated) {
        console.log('WidgetStore: Auto-loading widgets (first time)');
        state.loadWidgets();
      }
    },
    
    _handleDisconnect: () => {
      console.log('WidgetStore: Clearing saving flags due to disconnect');
      set({
        creatingWidget: false,
        savingWidget: false,
        publishingWidget: false,
        deletingWidget: false,
        generatingEmbed: false,
        widgetsLoading: false,
        selectedWidgetLoading: false
      });
    },

    _handleAuthReset: () => {
      console.log('WidgetStore: Resetting all state due to auth reset');

      set({
        widgets: [],
        widgetsLoading: false,
        selectedWidget: null,
        selectedWidgetLoading: false,
        creatingWidget: false,
        savingWidget: false,
        publishingWidget: false,
        deletingWidget: false,
        embedCodes: {},
        generatingEmbed: false,
        selectedType: 'all',
        isConnected: false,
        _initialized: false
      });
    }
  }))
);

// ================================
// SOCKET EVENT LISTENER SETUP (Simplified)
// ================================

export const initializeWidgetStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }
  
  console.log('WidgetStore: Setting up socket event listeners');
  
  const { universalHandler } = socketManager;
  
  // Set up universal response listener for widget events
  const handleSocketResponse = (responseData) => {
    const eventName = responseData.event;
    const store = useWidgetStore.getState();
    
    switch (eventName) {
      // SUCCESS EVENTS
      case SOCKET_EVENTS.WIDGETS_LOADED:
        store._setWidgetsLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.WIDGET_DETAILS_LOADED:
        store._setWidgetDetailsLoaded(responseData.data);
        break;
        
      case SOCKET_EVENTS.WIDGET_CREATED:
        const widgetData = responseData.data?.widget || responseData.data || responseData;
        store._addWidget(widgetData);
        break;
        
      case SOCKET_EVENTS.WIDGET_UPDATED:
        store._updateWidget(responseData.data);
        break;

      case SOCKET_EVENTS.WIDGET_PUBLISHED:
        store._publishWidget(responseData.data);
        break;

      case SOCKET_EVENTS.WIDGET_STATUS_TOGGLED:
        store._updateWidget(responseData.data);
        break;

      case SOCKET_EVENTS.WIDGET_DELETED:
        store._removeWidget(responseData.data.widget_id);
        break;
        
      case SOCKET_EVENTS.EMBED_CODE_GENERATED:
        store._setEmbedCodeGenerated(responseData.data);
        break;
        
      // ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_WIDGETS_FAILED:
        useWidgetStore.setState({ widgetsLoading: false });
        break;

      case SOCKET_EVENTS.GET_WIDGET_DETAILS_FAILED:
        useWidgetStore.setState({ selectedWidgetLoading: false });
        break;

      case SOCKET_EVENTS.CREATE_WIDGET_FAILED:
        useWidgetStore.setState({ creatingWidget: false });
        break;

      case SOCKET_EVENTS.UPDATE_WIDGET_FAILED:
        useWidgetStore.setState({ savingWidget: false });
        break;

      case SOCKET_EVENTS.PUBLISH_WIDGET_FAILED:
        useWidgetStore.setState({ publishingWidget: false });
        break;

      case SOCKET_EVENTS.TOGGLE_WIDGET_STATUS_FAILED:
        useWidgetStore.setState({ savingWidget: false });
        break;

      case SOCKET_EVENTS.DELETE_WIDGET_FAILED:
        useWidgetStore.setState({ deletingWidget: false });
        break;

      case SOCKET_EVENTS.GET_EMBED_CODE_FAILED:
        useWidgetStore.setState({ generatingEmbed: false });
        break;
        
      default:
        // Not a widget-related event, ignore
        break;
    }
  };
  
  // Set up individual event listeners
  const widgetEvents = [
    SOCKET_EVENTS.WIDGETS_LOADED,
    SOCKET_EVENTS.WIDGET_DETAILS_LOADED,
    SOCKET_EVENTS.WIDGET_CREATED,
    SOCKET_EVENTS.WIDGET_UPDATED,
    SOCKET_EVENTS.WIDGET_PUBLISHED,
    SOCKET_EVENTS.WIDGET_STATUS_TOGGLED,
    SOCKET_EVENTS.WIDGET_DELETED,
    SOCKET_EVENTS.EMBED_CODE_GENERATED,
    // Error events
    SOCKET_EVENTS.GET_WIDGETS_FAILED,
    SOCKET_EVENTS.GET_WIDGET_DETAILS_FAILED,
    SOCKET_EVENTS.CREATE_WIDGET_FAILED,
    SOCKET_EVENTS.UPDATE_WIDGET_FAILED,
    SOCKET_EVENTS.PUBLISH_WIDGET_FAILED,
    SOCKET_EVENTS.TOGGLE_WIDGET_STATUS_FAILED,
    SOCKET_EVENTS.DELETE_WIDGET_FAILED,
    SOCKET_EVENTS.GET_EMBED_CODE_FAILED
  ];
  
  widgetEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      handleSocketResponse({ event: eventName, data });
    });
  });
  
  // Set up auth reset and disconnect listeners
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      useWidgetStore.getState()._handleAuthReset();
    });
    window.addEventListener('socket-disconnected', () => {
      useWidgetStore.getState()._handleDisconnect();
    });
  }
  
  // Initialize the store
  useWidgetStore.getState()._initialize(socketManager);
  
  console.log('WidgetStore: Socket event listeners initialized');
};

export default useWidgetStore;